import React, { useState, useEffect } from "react";
import { Edit, Plus, Trash2, X, Lock } from "lucide-react";
import api from "../api";
import { K } from "../theme";
import { Bg, Bt, Sl, Inp } from "./index";
import { confirmDialog, notify } from "../notify";

// Phase 1 of the Zoho-style salary structure: a Settings UI over the
// salary_components master library. Admins can add custom earnings,
// deductions, reimbursements and benefits without code changes.
//
// Seed (system) components can be edited (defaults, flags) but not
// deleted — DELETE on a system row auto-deactivates so payroll wiring
// in later phases can't reference a missing row.

const CATEGORIES = [
  { key: 'earning',       label: 'Earnings',       help: "What's paid to the employee. Show on the salary side of the payslip.", accent: K.g },
  { key: 'deduction',     label: 'Deductions',     help: "What's deducted from gross — statutory (PF/ESI/PT/TDS) and recovery.", accent: K.r },
  { key: 'reimbursement', label: 'Reimbursements', help: "Bill-against-entitlement allowances — non-taxable when bills are produced.", accent: K.bl },
  { key: 'benefit',       label: 'Benefits',       help: "Employer-side cost on the CTC view — not paid out in the monthly payslip.", accent: K.pu },
];

const CALC_OPTIONS = [
  { v: 'fixed',            l: 'Fixed amount' },
  { v: 'percent_of_basic', l: '% of Basic' },
  { v: 'percent_of_ctc',   l: '% of CTC (annual gross)' },
  { v: 'percent_of_gross', l: '% of Gross (monthly)' },
];
const FREQ_OPTIONS = [
  { v: 'monthly',   l: 'Monthly' },
  { v: 'quarterly', l: 'Quarterly' },
  { v: 'annual',    l: 'Annual' },
  { v: 'one_time',  l: 'One-time' },
];
const fmtCalc = (calc, val) =>
  calc === 'fixed' ? `₹${Number(val || 0).toLocaleString('en-IN')}` :
  calc === 'percent_of_basic' ? `${val}% of Basic` :
  calc === 'percent_of_ctc'   ? `${val}% of CTC` :
  calc === 'percent_of_gross' ? `${val}% of Gross` : `${val}`;

export const SalaryComponentsManager = () => {
  const [rows, setRows] = useState(null);
  const [edit, setEdit] = useState(null); // null | { category } (new) | row (edit)
  const [msg, setMsg] = useState("");

  const load = () =>
    api.get('/api/salary-components')
      .then(d => setRows(d || []))
      .catch(e => setMsg('Error: ' + (e.message || 'load failed')));

  useEffect(() => { load(); }, []);

  const flash = (m, e = false) => { setMsg((e ? 'Error: ' : '') + m); setTimeout(() => setMsg(""), 3000); };

  const del = async (c) => {
    const msg = c.system
      ? `"${c.name}" is a system component and can't be deleted. Deactivate it instead?`
      : `Delete custom component "${c.name}"? This cannot be undone.`;
    if (!(await confirmDialog(msg))) return;
    try {
      const r = await api.del('/api/salary-components/' + c.code);
      flash(r.deleted ? 'Component deleted' : (r.deactivated ? 'Component deactivated' : 'Done'));
      load();
    } catch (e) { flash(e.message || 'Failed', true); }
  };

  const toggleActive = async (c) => {
    try { await api.put('/api/salary-components/' + c.code, { active: !c.active }); load(); }
    catch (e) { flash(e.message || 'Failed', true); }
  };

  if (!rows) return <div className="text-t3 text-[14px] p-4">Loading…</div>;

  const byCategory = (cat) => rows.filter(r => r.category === cat);

  return (
    <div>
      <div className="text-[14px] text-t2 mb-4">
        Master library of salary components. Used by payroll templates and per-employee salary structures (coming in the next phases). System components ship with the product — you can change their values and flags, but not delete them.
      </div>

      <div className="grid gap-5">
        {CATEGORIES.map(cat => {
          const list = byCategory(cat.key);
          return (
            <div key={cat.key}>
              <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <span className="w-[6px] h-[18px] rounded-full" style={{ background: cat.accent }} />
                  <div>
                    <div className="text-[15px] font-bold text-t0">{cat.label}</div>
                    <div className="text-[14px] text-t3 max-w-[560px]">{cat.help}</div>
                  </div>
                </div>
                <Bt s="sm" icon={Plus} onClick={() => setEdit({ category: cat.key })}>Add {cat.label.replace(/s$/, '')}</Bt>
              </div>
              <div className="rounded-xl border border-b0 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse text-[14px]">
                    <thead><tr className="bg-bg2">
                      <th className="px-2 py-[6px] text-left text-t3 text-[12px] uppercase">Code</th>
                      <th className="px-2 py-[6px] text-left text-t3 text-[12px] uppercase">Name</th>
                      <th className="px-2 py-[6px] text-left text-t3 text-[12px] uppercase">Calculation</th>
                      <th className="px-2 py-[6px] text-left text-t3 text-[12px] uppercase">Frequency</th>
                      <th className="px-2 py-[6px] text-center text-t3 text-[12px] uppercase">Taxable</th>
                      <th className="px-2 py-[6px] text-center text-t3 text-[12px] uppercase">PF</th>
                      <th className="px-2 py-[6px] text-center text-t3 text-[12px] uppercase">ESI</th>
                      <th className="px-2 py-[6px] text-center text-t3 text-[12px] uppercase">Gratuity</th>
                      <th className="px-2 py-[6px] text-center text-t3 text-[12px] uppercase">Status</th>
                      <th className="px-2 py-[6px] text-right text-t3 text-[12px] uppercase"></th>
                    </tr></thead>
                    <tbody>
                      {list.map(c => (
                        <tr key={c.code} className="border-b border-b0 hover:bg-sh">
                          <td className="px-2 py-[6px] font-mono text-[14px] text-t1 flex items-center gap-1">
                            {c.code}
                            {c.system && <Lock size={9} className="text-t3" title="System — cannot be deleted" />}
                          </td>
                          <td className="px-2 py-[6px] text-[14px] font-semibold text-t0">{c.name}</td>
                          <td className="px-2 py-[6px] text-[14px] text-t1">{fmtCalc(c.calcType, c.defaultValue)}</td>
                          <td className="px-2 py-[6px] text-[14px] text-t2 capitalize">{c.payFrequency.replace('_', ' ')}</td>
                          <td className="px-2 py-[6px] text-center text-[14px]">{c.isTaxable ? '✓' : '—'}</td>
                          <td className="px-2 py-[6px] text-center text-[14px]">{c.consideredForPf ? '✓' : '—'}</td>
                          <td className="px-2 py-[6px] text-center text-[14px]">{c.consideredForEsi ? '✓' : '—'}</td>
                          <td className="px-2 py-[6px] text-center text-[14px]">{c.consideredForGratuity ? '✓' : '—'}</td>
                          <td className="px-2 py-[6px] text-center">
                            {c.active ? <Bg c={K.g} sm dot>Active</Bg> : <Bg c={K.t3} sm>Inactive</Bg>}
                          </td>
                          <td className="px-2 py-[6px] text-right whitespace-nowrap">
                            <Bt v="ghost" s="xs" icon={Edit} onClick={() => setEdit(c)} />
                            <Bt v="ghost" s="xs" onClick={() => toggleActive(c)}>{c.active ? 'Deactivate' : 'Activate'}</Bt>
                            <Bt v="ghost" s="xs" icon={Trash2} onClick={() => del(c)} />
                          </td>
                        </tr>
                      ))}
                      {list.length === 0 && (
                        <tr><td colSpan={10} className="text-center p-4 text-t3 text-[14px]">No {cat.label.toLowerCase()} yet — click <b className="text-t0">Add {cat.label.replace(/s$/, '')}</b>.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {msg && <div className={`mt-3 text-[14px] font-semibold ${msg.startsWith('Error') ? 'text-r' : 'text-g'}`}>{msg}</div>}

      {edit && <ComponentModal cur={edit.id ? edit : null} defaultCategory={edit.category || edit.category} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
    </div>
  );
};

// ── Add / Edit component modal ──
export const ComponentModal = ({ cur, defaultCategory, onClose, onSaved }) => {
  const isEdit = !!cur?.id;
  const isSystem = isEdit && !!cur.system;
  const init = isEdit ? {
    code: cur.code, name: cur.name, category: cur.category,
    calcType: cur.calcType, defaultValue: String(cur.defaultValue ?? 0),
    isTaxable: cur.isTaxable, consideredForPf: cur.consideredForPf,
    consideredForEsi: cur.consideredForEsi, consideredForGratuity: cur.consideredForGratuity,
    proratedForLop: cur.proratedForLop,
    payFrequency: cur.payFrequency, description: cur.description || '',
    active: cur.active, sortOrder: String(cur.sortOrder ?? 500),
  } : {
    code: '', name: '', category: defaultCategory || 'earning',
    calcType: 'fixed', defaultValue: '0',
    isTaxable: true, consideredForPf: false, consideredForEsi: true,
    consideredForGratuity: false, proratedForLop: true,
    payFrequency: 'monthly', description: '',
    active: true, sortOrder: '500',
  };
  const [f, setF] = useState(init);
  const [saving, setSaving] = useState(false); const [err, setErr] = useState('');

  const save = async () => {
    if (!f.name.trim()) return setErr('Name is required');
    if (!isEdit && !f.code.trim()) return setErr('Code is required');
    if (!CATEGORIES.find(c => c.key === f.category)) return setErr('Pick a category');
    setSaving(true); setErr('');
    const body = {
      name: f.name.trim(),
      category: f.category,
      calcType: f.calcType,
      defaultValue: parseFloat(f.defaultValue) || 0,
      isTaxable: !!f.isTaxable,
      consideredForPf: !!f.consideredForPf,
      consideredForEsi: !!f.consideredForEsi,
      consideredForGratuity: !!f.consideredForGratuity,
      proratedForLop: !!f.proratedForLop,
      payFrequency: f.payFrequency,
      description: f.description || null,
      sortOrder: parseInt(f.sortOrder) || 500,
      active: !!f.active,
    };
    try {
      if (isEdit) await api.put('/api/salary-components/' + cur.code, body);
      else await api.post('/api/salary-components', { code: f.code.trim(), ...body });
      onSaved();
    } catch (e) { setErr(e.message || 'Save failed'); } finally { setSaving(false); }
  };

  const lbl = "text-[14px] text-t2 mb-[3px]";
  return (
    <div className="fixed inset-0 animate-fadeIn bg-black/35 z-[200] flex items-center justify-center p-3" onMouseDown={onClose}>
      <div className="animate-modalIn bg-bg1 border border-b1 rounded-2xl p-4 sm:p-6 w-full max-w-[936px] max-h-[92vh] overflow-auto" onMouseDown={e => e.stopPropagation()}>
        <div className="flex justify-between mb-1">
          <span className="text-[18px] font-extrabold text-t0">{isEdit ? 'Edit Salary Component' : 'Add Salary Component'}</span>
          <button onClick={onClose} className="bg-transparent border-0 text-t3 cursor-pointer"><X size={18} /></button>
        </div>
        {isSystem && <div className="text-[14px] text-t3 italic mb-3 flex items-center gap-1"><Lock size={10}/> System component — code and category are fixed; values and flags can still be tuned.</div>}
        {!isSystem && <div className="text-[14px] text-t3 mb-3" />}

        <div className="text-[14px] font-bold text-t3 uppercase tracking-wide mb-2">Basics</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-[10px] mb-4">
          <div><div className={lbl}>Code <span className="text-r">*</span></div>
            <Inp value={f.code} onChange={v => setF(p => ({ ...p, code: (v || '').toUpperCase().replace(/[^A-Z0-9_]/g, '_').slice(0, 24) }))} placeholder="e.g. PERF_BONUS" />
            <div className="text-[12px] text-t3 mt-[2px]">{isEdit ? 'Code cannot be changed.' : 'A short identifier — letters, digits, underscore.'}</div>
          </div>
          <div><div className={lbl}>Name <span className="text-r">*</span></div><Inp value={f.name} onChange={v => setF(p => ({ ...p, name: v }))} placeholder="e.g. Performance Bonus" /></div>
          <div><div className={lbl}>Category {isSystem && <Lock size={9} className="inline text-t3" />}</div>
            <Sl options={CATEGORIES.map(c => ({ v: c.key, l: c.label }))} value={f.category} onChange={v => !isSystem && setF(p => ({ ...p, category: v }))} />
          </div>
          <div><div className={lbl}>Pay Frequency</div><Sl options={FREQ_OPTIONS} value={f.payFrequency} onChange={v => setF(p => ({ ...p, payFrequency: v }))} /></div>
        </div>

        <div className="text-[14px] font-bold text-t3 uppercase tracking-wide mb-2">Calculation</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-[10px] mb-4">
          <div><div className={lbl}>Calculation type</div><Sl options={CALC_OPTIONS} value={f.calcType} onChange={v => setF(p => ({ ...p, calcType: v }))} /></div>
          <div><div className={lbl}>{f.calcType === 'fixed' ? 'Default amount (₹)' : 'Default rate (%)'}</div><Inp type="number" value={f.defaultValue} onChange={v => setF(p => ({ ...p, defaultValue: v }))} /></div>
        </div>

        <div className="text-[14px] font-bold text-t3 uppercase tracking-wide mb-2">Statutory &amp; Tax Flags</div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-[8px] mb-4">
          {[
            ['isTaxable', 'Taxable income'],
            ['consideredForPf', 'Counts for PF'],
            ['consideredForEsi', 'Counts for ESI'],
            ['consideredForGratuity', 'Counts for Gratuity'],
            ['proratedForLop', 'Prorated for LOP'],
            ['active', 'Active'],
          ].map(([k, l]) => (
            <label key={k} className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={!!f[k]} onChange={e => setF(p => ({ ...p, [k]: e.target.checked }))} />
              <span className="text-[14px] text-t1">{l}</span>
            </label>
          ))}
        </div>

        <div className="text-[14px] font-bold text-t3 uppercase tracking-wide mb-2">Notes</div>
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_120px] gap-[10px] mb-4">
          <div><div className={lbl}>Description</div><Inp value={f.description} onChange={v => setF(p => ({ ...p, description: v }))} placeholder="What this component is for…" /></div>
          <div><div className={lbl}>Sort order</div><Inp type="number" value={f.sortOrder} onChange={v => setF(p => ({ ...p, sortOrder: v }))} /></div>
        </div>

        {err && <div className="text-[14px] text-r mb-2">{err}</div>}
        <div className="flex gap-[6px]">
          <Bt full onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Bt>
          <Bt v="secondary" full onClick={onClose}>Cancel</Bt>
        </div>
      </div>
    </div>
  );
};
